import { showErrorMessage } from 'actions/messages';
import { assistantApi } from 'services/api/assistant-api';
import history from '../../../../history';
import { loadProjectRequest } from 'components/admin/projects/details/store/actions';
import { getCurrentProject } from 'components/admin/projects/details/store/selectors';
import {
  getAccessToken,
  getClient,
  getCurrentUser,
} from 'selectors/authentication';
import { createRfaFromScreenshot } from 'services/api/agent-api';
import resizeImage from 'services/utils/resize-image';
import { draftPath, friendlyFileError, isRetryable } from '../attachments';
import { startNewRfaWithPrefill } from 'components/admin/projects/details/project-rfa-log/store/actions';
import { startNewRfiWithPrefill } from 'components/admin/projects/details/project-rfi-log/store/actions';
import { startNewSubmittalWithPrefill } from 'components/admin/projects/details/project-submittal-log/store/actions';
import { startNewPunchListWithPrefill } from 'components/admin/projects/details/project-punch-list/store/actions';
import { startNewDailyReportWithPrefill } from 'components/admin/projects/details/project-daily-report-log/store/actions';
import { startNewFieldReportWithPrefill } from 'components/admin/projects/details/project-daily-report-log/field-reports/store/actions';
import { startNewProjectReportWithPrefill } from 'components/admin/projects/details/project-daily-report-log/store/actions';
import {
  ASSISTANT_SET_OPEN,
  ASSISTANT_LOAD_MESSAGES,
  ASSISTANT_LOAD_MESSAGES_SUCCESS,
  ASSISTANT_LOAD_MESSAGES_FAILURE,
  ASSISTANT_SEND_MESSAGE,
  ASSISTANT_SEND_MESSAGE_SUCCESS,
  ASSISTANT_SEND_MESSAGE_FAILURE,
  ASSISTANT_CLEAR_MESSAGES,
} from './action-types';

export function buildAssistantContext() {
  const path = window.location.pathname;
  const match = path.match(/\/admin\/projects\/(\d+)/);
  return {
    project_id: match ? parseInt(match[1], 10) : null,
    current_path: path,
  };
}

function normalizeMessage(msg) {
  return {
    id: msg.id || `msg-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    role: msg.role,
    content: msg.content,
    createdAt: msg.created_at || msg.createdAt || new Date().toISOString(),
  };
}

export function setAssistantOpen(open) {
  return {
    type: ASSISTANT_SET_OPEN,
    payload: open,
  };
}

export function toggleAssistantOpen() {
  return (dispatch, getState) => {
    const current = getState().assistant.isOpen;
    dispatch(setAssistantOpen(!current));
  };
}

export function assistantSendMessage(userMessage) {
  return {
    type: ASSISTANT_SEND_MESSAGE,
    payload: { userMessage },
  };
}

export function assistantSendMessageSuccess(payload) {
  return {
    type: ASSISTANT_SEND_MESSAGE_SUCCESS,
    payload:
      typeof payload === 'object' && payload.assistantMessage
        ? payload
        : { assistantMessage: payload, conversationId: null },
  };
}

export function assistantSendMessageFailure(error) {
  return {
    type: ASSISTANT_SEND_MESSAGE_FAILURE,
    payload: error,
  };
}

export function clearAssistantMessages() {
  return {
    type: ASSISTANT_CLEAR_MESSAGES,
  };
}

export function loadAssistantMessages() {
  return (dispatch) => {
    dispatch({ type: ASSISTANT_LOAD_MESSAGES });
    return assistantApi.getMessages().then((response) => {
      if (response.ok) {
        const messages = (response.messages || []).map(normalizeMessage);
        dispatch({
          type: ASSISTANT_LOAD_MESSAGES_SUCCESS,
          payload: {
            messages,
            conversationId: response.conversationId,
            pendingNavigation: response.pendingNavigation,
          },
        });
      } else {
        dispatch({
          type: ASSISTANT_LOAD_MESSAGES_FAILURE,
          payload: response.errors,
        });
        dispatch(showErrorMessage(response.errors));
      }
      return response;
    });
  };
}

export function clearAssistantChat() {
  return (dispatch) => {
    return assistantApi.clearChat().then((response) => {
      if (response.ok) {
        dispatch(clearAssistantMessages());
      } else {
        dispatch(showErrorMessage(response.errors));
      }
      return response;
    });
  };
}

export function sendAssistantMessage(content) {
  return (dispatch, getState) => {
    const { messages, conversationId } = getState().assistant;
    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      createdAt: new Date().toISOString(),
    };

    dispatch(assistantSendMessage(userMessage));

    // Turns that only exist in this browser session (file turns, see below) are
    // not part of the server-side conversation, so they are not sent back to it.
    const updatedMessages = [
      ...messages.filter((msg) => !msg.local),
      userMessage,
    ];
    const context = buildAssistantContext();

    return assistantApi
      .sendMessage(updatedMessages, conversationId, context)
      .then((response) => {
        if (response.ok) {
          const assistantMessage = {
            id: `assistant-${Date.now()}`,
            role: 'assistant',
            content: response.message,
            createdAt: new Date().toISOString(),
          };
          dispatch(
            assistantSendMessageSuccess({
              assistantMessage,
              conversationId: response.conversationId,
              navigation: response.navigation,
            })
          );
          if (response.navigation && response.navigation.path) {
            history.push(response.navigation.path);
            if (response.navigation.action === 'create') {
              const projectIdMatch = response.navigation.path.match(
                /\/admin\/projects\/(\d+)\//
              );
              const projectId = projectIdMatch ? projectIdMatch[1] : null;
              const formPrefill = response.navigation.form_prefill || {};
              const docType = response.navigation.document_type;
              if (projectId) {
                dispatch(loadProjectRequest(projectId)).then((loadResponse) => {
                  if (loadResponse.ok) {
                    const state = getState();
                    const currentProject = getCurrentProject(state);
                    const currentUser = getCurrentUser(state);
                    if (currentProject && currentUser) {
                      if (docType === 'rfa') {
                        dispatch(
                          startNewRfaWithPrefill(
                            currentUser,
                            currentProject,
                            formPrefill
                          )
                        );
                      } else if (docType === 'rfi') {
                        dispatch(
                          startNewRfiWithPrefill(
                            currentUser,
                            currentProject,
                            formPrefill
                          )
                        );
                      } else if (docType === 'submittal') {
                        dispatch(
                          startNewSubmittalWithPrefill(
                            currentUser,
                            currentProject,
                            formPrefill
                          )
                        );
                      } else if (docType === 'punch_list') {
                        dispatch(
                          startNewPunchListWithPrefill(
                            currentUser,
                            currentProject,
                            formPrefill
                          )
                        );
                      } else if (docType === 'daily_report') {
                        dispatch(
                          startNewDailyReportWithPrefill(
                            currentUser,
                            currentProject,
                            formPrefill
                          )
                        );
                      } else if (docType === 'field_report') {
                        dispatch(
                          startNewFieldReportWithPrefill(
                            currentUser,
                            currentProject,
                            formPrefill
                          )
                        );
                      } else if (docType === 'project_report') {
                        dispatch(
                          startNewProjectReportWithPrefill(
                            currentUser,
                            currentProject,
                            formPrefill
                          )
                        );
                      }
                    }
                  }
                });
              }
            }
          }
        } else {
          const errorMessage = response.errors || 'Failed to send message';
          dispatch(assistantSendMessageFailure(errorMessage));
          dispatch(showErrorMessage(errorMessage));
        }
        return response;
      });
  };
}

const localMessage = (role, content, extra = {}) => ({
  id: `${role}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  role,
  content,
  createdAt: new Date().toISOString(),
  // Shown in this session only: file turns go to the agent, not to /assistant/chat,
  // so they are not saved to the chat history.
  local: true,
  ...extra,
});

function draftCreatedText(data, file, projectId, projectName) {
  const where = projectName ? ` in ${projectName}` : '';
  const link = `[Open the draft](${draftPath(projectId, data.rfa_id)})`;
  const revision =
    data.action === 'revision_created'
      ? ' It is a new revision linked to an existing RFA.'
      : '';
  // Deliberately says nothing about who can see the draft: that is decided by
  // RfaPolicy::Scope (creator, assigned company, client contributors, ...), which
  // is broader than "you and admins". Only claim what is certain.
  const saved =
    "It's saved as a draft. Submit it from the RFA Log when it's ready.";
  const unlinked = data.limited_visibility
    ? " I couldn't link it to your account, so it may not appear in your own RFA Log. Ask an admin if you can't find it."
    : '';
  const visibility = `${saved}${unlinked}`;
  return `I created a draft RFA from **${file.name}**${where}.${revision}\n\n${link}\n\n${visibility}`;
}

// Send a file (and the typed request, if any) to the agent, which reads it and
// creates a DRAFT RFA in `projectId`. The exchange is shown in the thread as
// local-only turns. Resolves to { ok, status, retryable }.
export function sendAssistantFileMessage({
  file,
  content,
  projectId,
  projectName,
}) {
  return async (dispatch, getState) => {
    const typed = (content || '').trim();
    dispatch(
      assistantSendMessage(
        localMessage('user', typed || 'Create an RFA from this file.', {
          attachmentName: file.name,
        })
      )
    );

    const state = getState();
    const user = getCurrentUser(state);
    const upload = /^image\//.test(file.type) ? await resizeImage(file) : file;
    const response = await createRfaFromScreenshot(
      projectId,
      upload,
      getAccessToken(state),
      getClient(state),
      user && user.uid,
      typed
    );

    if (response.ok) {
      dispatch(
        assistantSendMessageSuccess({
          assistantMessage: localMessage(
            'assistant',
            draftCreatedText(response.data, file, projectId, projectName)
          ),
          conversationId: null,
        })
      );
      return { ok: true, status: 201, retryable: false };
    }

    const status = response.status === undefined ? null : response.status;
    dispatch(
      assistantSendMessageSuccess({
        assistantMessage: localMessage(
          'assistant',
          friendlyFileError(status, response.errors, response.declined),
          { isError: true }
        ),
        conversationId: null,
      })
    );
    return { ok: false, status, retryable: isRetryable(status) };
  };
}
